import { Suspense, lazy } from "react";
import { AppLayout } from "@/components/AppLayout";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useThemePreferences } from "@/hooks/useThemePreferences";
import { TourProvider } from "@/components/TourProvider";
import { WithGlobalErrorBoundary } from "@/components/GlobalErrorBoundary";
import { GlobalRuntimeMonitor } from "@/components/GlobalRuntimeMonitor";
import { OrgProvider } from "@/contexts/OrgContext";
import { RolePreviewProvider } from "@/contexts/RolePreviewContext";
import { RolePreviewBanner } from "@/components/RolePreviewBanner";
import { FeatureFlagsProvider } from "@/contexts/FeatureFlagsContext";
import { AnalyticsProvider } from "@/components/AnalyticsProvider";
import { OfflineIndicator } from "@/components/OfflineIndicator";
import { Loader2 } from "lucide-react";
import { PWAUpdatePrompt } from "@/components/PWAUpdatePrompt";

// Core routes - loaded synchronously for fastest initial render
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import Jobs from "./pages/Jobs";

// Lazy-loaded routes - only loaded when navigated to
const JobDetail = lazy(() => import("./pages/JobDetail"));
const Schedule = lazy(() => import("./pages/Schedule"));
const More = lazy(() => import("./pages/More"));
const FieldPack = lazy(() => import("./pages/FieldPack"));
const FieldQADashboard = lazy(() => import("./pages/FieldQADashboard"));
const DailyLog = lazy(() => import("./pages/DailyLog"));
const DailyLogSummary = lazy(() => import("./pages/DailyLogSummary"));
const JobKPIDashboard = lazy(() => import("./pages/JobKPIDashboard"));
const ScheduleForecast = lazy(() => import("./pages/ScheduleForecast"));
const VendorPerformanceDashboard = lazy(() => import("./pages/VendorPerformanceDashboard"));
const TeamProductivityDashboard = lazy(() => import("./pages/TeamProductivityDashboard"));
const ResourceLoadPlanner = lazy(() => import("./pages/ResourceLoadPlanner"));
const ProjectHealthDashboard = lazy(() => import("./pages/ProjectHealthDashboard"));
const UserPreferences = lazy(() => import("./pages/UserPreferences"));
const UserInvitations = lazy(() => import("./pages/UserInvitations"));
const RolesPermissions = lazy(() => import("./pages/RolesPermissions"));
const InviteAccept = lazy(() => import("./pages/InviteAccept"));
const EmailRouting = lazy(() => import("./pages/EmailRouting"));
const TodayBoard = lazy(() => import("./pages/TodayBoard"));
const Procurement = lazy(() => import("./pages/Procurement"));
const ChangeOrderNew = lazy(() => import("./pages/ChangeOrderNew"));
const ChangeOrderEdit = lazy(() => import("./pages/ChangeOrderEdit"));
const Estimates = lazy(() => import("./pages/Estimates"));
const EstimateNew = lazy(() => import("./pages/EstimateNew"));
const EstimateEdit = lazy(() => import("./pages/EstimateEdit"));
const EstimateDetail = lazy(() => import("./pages/EstimateDetail"));
const NotFound = lazy(() => import("./pages/NotFound"));
const FeatureFlags = lazy(() => import("./pages/FeatureFlags"));
const Calendar = lazy(() => import("./pages/Calendar"));
const TVCalendarDisplay = lazy(() => import("./pages/TVCalendarDisplay"));
const Install = lazy(() => import("./pages/Install"));
const CrewAvailability = lazy(() => import("./pages/CrewAvailability"));
const RFIs = lazy(() => import("./pages/RFIs"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const ReportsAnalytics = lazy(() => import("./pages/ReportsAnalytics"));
import { DesktopOnlyGuard } from "@/components/DesktopOnlyGuard";
const AdminPanel = lazy(() => import("./pages/AdminPanel"));
const TimeCards = lazy(() => import("./pages/TimeCards"));
const TimeCardEntry = lazy(() => import("./pages/TimeCardEntry"));
const Messages = lazy(() => import("./pages/Messages"));
const GanttChart = lazy(() => import("./pages/GanttChart"));

const queryClient = new QueryClient();

// Loading fallback for lazy-loaded routes
function PageLoader() {
  return (
    <div className="flex h-screen w-full items-center justify-center bg-background">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}

function AppContent() {
  useThemePreferences();

  // No root caching SW — nothing to update on navigation.
  return (
    <AppLayout>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Core routes - synchronous */}
          <Route path="/" element={<Index />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/jobs" element={<Jobs />} />
          
          {/* Lazy-loaded routes */}
          <Route path="/today-board" element={<TodayBoard />} />
          <Route path="/job/:id" element={<JobDetail />} />
          <Route path="/jobs/:jobId/change-orders/new" element={<ChangeOrderNew />} />
          <Route path="/jobs/:jobId/change-orders/:changeOrderId/edit" element={<ChangeOrderEdit />} />
          <Route path="/schedule" element={<Schedule />} />
          <Route path="/field-pack" element={<FieldPack />} />
          <Route path="/field-qa" element={<FieldQADashboard />} />
          <Route path="/daily-log" element={<DailyLog />} />
          <Route path="/daily-log-summary" element={<DailyLogSummary />} />
          <Route path="/kpi-dashboard" element={<JobKPIDashboard />} />
          <Route path="/schedule-forecast" element={<ScheduleForecast />} />
          <Route path="/vendor-performance" element={<DesktopOnlyGuard><VendorPerformanceDashboard /></DesktopOnlyGuard>} />
          <Route path="/team-productivity" element={<DesktopOnlyGuard><TeamProductivityDashboard /></DesktopOnlyGuard>} />
          <Route path="/resource-load-planner" element={<DesktopOnlyGuard><ResourceLoadPlanner /></DesktopOnlyGuard>} />
          <Route path="/project-health" element={<DesktopOnlyGuard><ProjectHealthDashboard /></DesktopOnlyGuard>} />
          <Route path="/preferences" element={<UserPreferences />} />
          <Route path="/user-invitations" element={<UserInvitations />} />
          <Route path="/roles-permissions" element={<RolesPermissions />} />
          <Route path="/invite/:token" element={<InviteAccept />} />
          <Route path="/email-routing" element={<EmailRouting />} />
          <Route path="/procurement" element={<Procurement />} />
          <Route path="/estimates" element={<Estimates />} />
          <Route path="/estimates/new" element={<EstimateNew />} />
          <Route path="/estimates/:id" element={<EstimateDetail />} />
          <Route path="/estimates/:id/edit" element={<EstimateEdit />} />
          <Route path="/more" element={<More />} />
          <Route path="/feature-flags" element={<FeatureFlags />} />
          <Route path="/calendar" element={<Calendar />} />
          <Route path="/calendar/tv" element={<TVCalendarDisplay />} />
          <Route path="/install" element={<Install />} />
          <Route path="/crew-availability" element={<CrewAvailability />} />
          <Route path="/rfis" element={<RFIs />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/reports-analytics" element={<DesktopOnlyGuard><ReportsAnalytics /></DesktopOnlyGuard>} />
          <Route path="/admin" element={<AdminPanel />} />
          <Route path="/time-cards" element={<TimeCards />} />
          <Route path="/time-cards/:id" element={<TimeCardEntry />} />
          <Route path="/messages" element={<Messages />} />
          <Route path="/gantt" element={<DesktopOnlyGuard><GanttChart /></DesktopOnlyGuard>} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </AppLayout>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AnalyticsProvider>
          <OrgProvider>
            <RolePreviewProvider>
              <FeatureFlagsProvider>
                <TourProvider>
                  {/* Global error boundary to prevent blank screen crashes */}
                  <WithGlobalErrorBoundary>
                    <PWAUpdatePrompt />
                    <OfflineIndicator />
                    <RolePreviewBanner />
                    <GlobalRuntimeMonitor />
                    <AppContent />
                  </WithGlobalErrorBoundary>
                </TourProvider>
              </FeatureFlagsProvider>
            </RolePreviewProvider>
          </OrgProvider>
        </AnalyticsProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
